import { z } from 'zod';
import { templateAdvanceLogicV1Schema } from './advance-logic';
import { templateBaseSchema } from './base';

const optionItemObjectSchema = z.object({
  id: z.uuidv4(),
  label: z.string(),
  score: z.number().nullable(),
  includeInScoring: z.boolean(),
  enableNoteField: z.boolean(),
  childOption: z
    .object({
      type: z.enum(['radio-group', 'checkbox-group']),
      replaceParentScore: z.boolean(),
      items: z.array(
        z.object({
          id: z.uuidv4(),
          label: z.string(),
          score: z.number().nullable(),
          enableNoteField: z.boolean(),
        }),
      ),
    })
    .nullable(),
});

const optionItemsSchema = z.array(optionItemObjectSchema);

export type OptionItems = z.infer<typeof optionItemsSchema>;

// 支援有 score 或沒有 score 的選項項目
// 用於同時支援 row.choiceOptionItems（有 score）和 scoreLevel.choiceOptionItems（沒有 score）
type ChoiceOptionItemWithOptionalScore = Omit<OptionItems[number], 'score'> & {
  score?: number | null;
};
export type ChoiceOptionItem = Array<ChoiceOptionItemWithOptionalScore>;

export const templateScoreTableSchema = templateBaseSchema.extend({
  type: z.literal('scoreTable'),
  name: z.string(),
  showTitle: z.boolean(),
  showQuestionNumber: z.boolean(),
  showQuestionDescription: z.boolean(),
  enableRemark: z.boolean(),
  totalScoreCalculation: z.enum(['sum', 'average']),
  rows: z.array(
    z.object({
      id: z.uuidv4(),
      name: z.string(),
      options: z.enum([
        'radio-group',
        'number-input',
        'checkbox-group',
        'dropdown',
      ]),
      score: z.number().nullable(),
      description: z.string(),
      remark: z.string().optional(),
      required: z.boolean(),
      includeInTotal: z.boolean(),
      classificationMethod: z.enum(['custom', 'shared']),
      choiceOptionItems: optionItemsSchema,
      dropdownOptionItems: z.array(
        optionItemObjectSchema.omit({
          childOption: true,
        }),
      ),
      scoreLevels: z.array(
        z.object({
          id: z.uuidv4(),
          score: z.number().nullable(),
          choiceOptionItems: z.array(
            optionItemObjectSchema.omit({ score: true }),
          ),
        }),
      ),
      inputRange: z.object({
        min: z.number().nullable(),
        max: z.number().nullable(),
      }),
      enableDecimalPoint: z.boolean(),
      decimalPlaces: z.number(),
      invalidValueHandling: z.enum(['show-error', 'ignore']),
    }),
  ),
  totalScore: z.number().nullable(),
  advanceLogics: templateAdvanceLogicV1Schema,
  // each row value
  values: z.record(
    z.string(), // rowId 作為 key
    z.object({
      value: z
        .union([
          z.string(), // custom + radio-group, dropdown
          z.number(), // number-input
          z.array(z.string()), // shared + radio-group, checkbox-group (無論 shared/custom)
        ])
        .nullable(),
      childValues: z
        .record(
          z.string(), // parent option id
          z
            .union([
              z.string(), // childOption type === 'radio-group'
              z.array(z.string()), // childOption type === 'checkbox-group'
            ])
            .nullable(),
        )
        .nullable(),
      remark: z.string().optional(),
      dropdownNote: z.string().optional(), // dropdown note
      optionNotes: z.record(z.string(), z.string()).nullable(), // optionId -> note value
    }),
  ),
  // total score
  value: z.number().nullable(),
});

export type TemplateScoreTableV1 = z.infer<typeof templateScoreTableSchema>;

// Runtime 模式使用的值類型
export type ScoreTableValues = TemplateScoreTableV1['values'];
export type ScoreTableRowValue = NonNullable<ScoreTableValues>[string];
